// src/hooks/useIdleLogout.js
import { useEffect, useRef, useState } from "react";
import { logout } from "@/lib/auth/logout";

/**
 * useIdleLogout (healthcare-friendly)
 * - Deliberate actions only: pointerdown, keydown, focusin
 * - Idle threshold: 30 min (default)
 * - Grace modal: 2 min (default)
 * - Keeps running when tab is hidden so true inactivity still logs out
 */
export default function useIdleLogout({
  enabled = true,
  isAuthed = false,
  idleMs = 30 * 60 * 1000, // 30 minutes idle (healthcare-friendly)
  graceMs = 2 * 60 * 1000, // 2 minutes to respond
  redirectTo = "/login?logout=idle",
} = {}) {
  const [open, setOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(graceMs / 1000));

  const idleTimerRef = useRef(null);
  const graceTimerRef = useRef(null);
  const tickRef = useRef(null);

  const clearAll = () => {
    if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    if (graceTimerRef.current) window.clearTimeout(graceTimerRef.current);
    if (tickRef.current) window.clearInterval(tickRef.current);
    idleTimerRef.current = null;
    graceTimerRef.current = null;
    tickRef.current = null;
  };

  const hardLogout = async () => {
    try {
      await logout({ redirectTo });
    } catch {
      // logout() hard-redirects anyway
    }
  };

  const closeModalAndRearm = () => {
    clearAll();
    setOpen(false);
    setSecondsLeft(Math.ceil(graceMs / 1000));

    idleTimerRef.current = window.setTimeout(() => {
      // Show warning modal
      setOpen(true);

      const totalSec = Math.max(1, Math.ceil(graceMs / 1000));
      const startedAt = Date.now();
      setSecondsLeft(totalSec);

      tickRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        const left = Math.max(0, totalSec - elapsed);
        setSecondsLeft(left);
      }, 1000);

      graceTimerRef.current = window.setTimeout(() => {
        hardLogout();
      }, graceMs);
    }, idleMs);
  };

  const markActivity = () => {
    // If the modal is open, any deliberate action means “I’m here”
    // but we still require explicit choice to stay signed in.
    // For healthcare UX, we *close* the modal and restart idle timer.
    if (open) {
      closeModalAndRearm();
      return;
    }
    closeModalAndRearm();
  };

  const staySignedIn = () => {
    // explicit confirmation
    closeModalAndRearm();
  };

  const logoutNow = () => {
    hardLogout();
  };

  useEffect(() => {
    if (!enabled || !isAuthed) {
      clearAll();
      setOpen(false);
      return;
    }

    // Don’t run on auth screens
    const path = window.location.pathname || "";
    if (path.startsWith("/login") || path.startsWith("/auth/")) {
      clearAll();
      setOpen(false);
      return;
    }

    const onActivity = () => markActivity();

    // Treat active reading/scrolling on mobile as real activity too.
    const windowEvents = ["pointerdown", "keydown", "focusin", "touchstart"];
    windowEvents.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true })
    );
    document.addEventListener("scroll", onActivity, {
      passive: true,
      capture: true,
    });

    // Arm initially
    closeModalAndRearm();

    return () => {
      windowEvents.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("scroll", onActivity, true);
      clearAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isAuthed, idleMs, graceMs]);

  return { open, secondsLeft, staySignedIn, logoutNow };
}
